import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import id from "./locales/id";
import en from "./locales/en";

const dictionaries = { id, en };
const LANG_KEY = "mandala_lang";

const LanguageContext = createContext({
  lang: "id",
  setLang: () => {},
  t: (key) => key,
  locale: "id-ID",
});

const getByPath = (obj, path) =>
  path.split(".").reduce((acc, part) => (acc && acc[part] !== undefined ? acc[part] : undefined), obj);

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    try {
      const saved = localStorage.getItem(LANG_KEY);
      if (saved === "id" || saved === "en") return saved;
    } catch { /* ignore */ }
    return "id";
  });

  const setLang = useCallback((next) => {
    const value = next === "en" ? "en" : "id";
    setLangState(value);
    try {
      localStorage.setItem(LANG_KEY, value);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang === "en" ? "en" : "id";
  }, [lang]);

  const t = useCallback(
    (key, vars) => {
      const dict = dictionaries[lang] || dictionaries.id;
      let text = getByPath(dict, key);
      if (text === undefined) {
        text = getByPath(dictionaries.id, key);
      }
      if (typeof text !== "string") return key;
      if (vars) {
        Object.entries(vars).forEach(([k, v]) => {
          text = text.replace(new RegExp(`\\{${k}\\}`, "g"), String(v));
        });
      }
      return text;
    },
    [lang]
  );

  const value = useMemo(
    () => ({
      lang,
      setLang,
      t,
      locale: lang === "en" ? "en-US" : "id-ID",
      dict: dictionaries[lang] || dictionaries.id,
    }),
    [lang, setLang, t]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => useContext(LanguageContext);
